import React, { useState, useEffect, memo, useRef, useMemo } from 'react';
import { FixedSizeList } from 'react-window';
import {
  Paper,
  IconButton,
  TextField,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Avatar,
  Typography,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import PrintIcon from '@mui/icons-material/Print';
import PersonIcon from '@mui/icons-material/Person';

// Generation colors with clear labels
const GENERATION_COLORS = [
  { color: '#8B5CF6', label: 'Gen 1 (Founders)' },    // Purple
  { color: '#3B82F6', label: 'Gen 2' },               // Blue
  { color: '#10B981', label: 'Gen 3' },               // Green
  { color: '#F59E0B', label: 'Gen 4' },               // Amber
  { color: '#EF4444', label: 'Gen 5' },               // Red
  { color: '#06B6D4', label: 'Gen 6' },               // Cyan
  { color: '#F97316', label: 'Gen 7' },               // Orange
  { color: '#EC4899', label: 'Gen 8' },               // Pink
  { color: '#6B7280', label: 'Gen 9+' },              // Gray
];

// IMPROVED: Calculate generations accurately
const calculateGenerations = (people) => {
  const generations = {};
  
  // Safety check
  if (!people || !Array.isArray(people) || people.length === 0) {
    return generations;
  }
  
  const personMap = {};
  
  // Create a map for quick lookup
  people.forEach(p => {
    if (p && p._id) {
      personMap[p._id] = p;
    }
  });
  
  // Helper to get parent IDs
  const getParentIds = (person) => {
    const parents = [];
    if (person.mother) {
      const motherId = person.mother._id || person.mother;
      if (personMap[motherId]) parents.push(motherId);
    }
    if (person.father) {
      const fatherId = person.father._id || person.father;
      if (personMap[fatherId]) parents.push(fatherId);
    }
    if (person.socialParent) {
      const socialId = person.socialParent._id || person.socialParent;
      if (personMap[socialId]) parents.push(socialId);
    }
    return parents;
  };
  
  // Helper to get spouse IDs
  const getSpouseIds = (person) => {
    const spouses = [];
    if (person.spouses && Array.isArray(person.spouses)) {
      person.spouses.forEach(s => {
        const spouseId = s._id || s;
        if (personMap[spouseId]) spouses.push(spouseId);
      });
    }
    return spouses;
  };
  
  // Helper to get kaweto IDs
  const getKawetoIds = (person) => {
    const kawetos = [];
    if (person.kawetos && Array.isArray(person.kawetos)) {
      person.kawetos.forEach(k => {
        const kawetoId = k._id || k;
        if (personMap[kawetoId]) kawetos.push(kawetoId);
      });
    }
    return kawetos;
  };
  
  // Helper to get children IDs
  const getChildrenIds = (person) => {
    const children = [];
    if (person.children && Array.isArray(person.children)) {
      person.children.forEach(c => {
        const childId = c._id || c;
        if (personMap[childId]) children.push(childId);
      });
    }
    return children;
  };
  
  // Find the true roots - people with no parents AND no spouse with parents
  // These are the founding generation
  const findRoots = () => {
    const roots = [];
    people.forEach(p => {
      const hasParents = getParentIds(p).length > 0;
      if (!hasParents) {
        // Check if any spouse has parents (meaning this person married into a lower generation)
        const spouseIds = getSpouseIds(p);
        const kawetoIds = getKawetoIds(p);
        const allPartners = [...spouseIds, ...kawetoIds];
        
        let spouseHasParents = false;
        for (const partnerId of allPartners) {
          const partner = personMap[partnerId];
          if (partner && getParentIds(partner).length > 0) {
            spouseHasParents = true;
            break;
          }
        }
        
        // Only true root if they AND their partners have no parents
        if (!spouseHasParents) {
          roots.push(p._id);
        }
      }
    });
    return roots;
  };
  
  // BFS to assign generations from roots
  const roots = findRoots();
  const queue = [];
  const visited = new Set();
  
  // Start with roots at generation 0
  roots.forEach(rootId => {
    queue.push({ id: rootId, gen: 0 });
  });
  
  while (queue.length > 0) {
    const { id, gen } = queue.shift();
    
    if (visited.has(id)) continue;
    visited.add(id);
    
    const person = personMap[id];
    if (!person) continue;
    
    // Assign generation
    generations[id] = gen;
    
    // Add children (gen + 1)
    const childrenIds = getChildrenIds(person);
    childrenIds.forEach(childId => {
      if (!visited.has(childId)) {
        queue.push({ id: childId, gen: gen + 1 });
      }
    });
    
    // Add spouses at SAME generation
    const spouseIds = getSpouseIds(person);
    spouseIds.forEach(spouseId => {
      if (!visited.has(spouseId) && !generations[spouseId]) {
        queue.push({ id: spouseId, gen: gen });
      }
    });
    
    // Add kawetos at SAME generation
    const kawetoIds = getKawetoIds(person);
    kawetoIds.forEach(kawetoId => {
      if (!visited.has(kawetoId) && !generations[kawetoId]) {
        queue.push({ id: kawetoId, gen: gen });
      }
    });
  }
  
  // Second pass: Handle anyone missed (connected via children -> parents)
  // This catches people who married into the family
  people.forEach(p => {
    if (generations[p._id] === undefined) {
      // Try to get generation from children (child's gen - 1)
      const childrenIds = getChildrenIds(p);
      for (const childId of childrenIds) {
        if (generations[childId] !== undefined) {
          generations[p._id] = generations[childId] - 1;
          break;
        }
      }
    }
  });
  
  // Third pass: Get generation from spouse if still unassigned
  people.forEach(p => {
    if (generations[p._id] === undefined) {
      const spouseIds = getSpouseIds(p);
      const kawetoIds = getKawetoIds(p);
      const allPartners = [...spouseIds, ...kawetoIds];
      
      for (const partnerId of allPartners) {
        if (generations[partnerId] !== undefined) {
          generations[p._id] = generations[partnerId];
          break;
        }
      }
    }
  });
  
  // Fourth pass: Get generation from parents (parent's gen + 1)
  people.forEach(p => {
    if (generations[p._id] === undefined) {
      const parentIds = getParentIds(p);
      for (const parentId of parentIds) {
        if (generations[parentId] !== undefined) {
          generations[p._id] = generations[parentId] + 1;
          break;
        }
      }
    }
  });
  
  // Final pass: Anyone still unassigned gets generation 0 (assume founder)
  people.forEach(p => {
    if (generations[p._id] === undefined) {
      generations[p._id] = 0;
    }
  });
  
  return generations;
};

// Get generation statistics
const getGenerationStats = (people, generations) => {
  const stats = {};
  people.forEach(p => {
    const gen = generations[p._id] || 0;
    if (!stats[gen]) {
      stats[gen] = { total: 0, alive: 0, dead: 0, male: 0, female: 0 };
    }
    stats[gen].total++;
    if (p.status === 'ALIVE') stats[gen].alive++;
    if (p.status === 'DEAD') stats[gen].dead++;
    if (p.gender === 'MALE') stats[gen].male++;
    if (p.gender === 'FEMALE') stats[gen].female++;
  });
  return stats;
};

// Row component
const Row = memo(({ index, style, data }) => {
  const {
    people,
    onSelect,
    setConfirmDeleteOpen,
    setPendingAction,
    isMobile,
    generations,
    canEdit,
    onEdit,
  } = data;
  
  const person = people[index];
  if (!person) return null;

  const generation = generations[person._id] || 0;
  const genIndex = Math.min(generation, GENERATION_COLORS.length - 1);
  const genColor = GENERATION_COLORS[genIndex].color;
  const genderIcon = person.gender === 'MALE' ? '♂' : person.gender === 'FEMALE' ? '♀' : '○';

  const handleEditClick = (e) => {
    e.stopPropagation();
    onEdit(person);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setPendingAction({ type: 'delete', person });
    setConfirmDeleteOpen(true);
  };

  // Mobile card view
  if (isMobile) {
    return (
      <Box
        style={{
          ...style,
          padding: '4px 8px',
        }}
      >
        <Paper
          onClick={() => onSelect(person)}
          style={{
            padding: '10px 12px',
            borderLeft: `4px solid ${genColor}`,
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          elevation={1}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box flex={1}>
              <Typography style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '4px' }}>
                {person.name}
              </Typography>
              
              <Box display="flex" gap="4px" flexWrap="wrap">
                <Chip
                  label={`G${generation + 1}`}
                  size="small"
                  style={{
                    backgroundColor: genColor,
                    color: '#fff',
                    fontWeight: '500',
                    height: '20px',
                    fontSize: '0.65rem',
                  }}
                />
                <Chip
                  label={person.gender === 'MALE' ? '♂' : person.gender === 'FEMALE' ? '♀' : '?'}
                  size="small"
                  variant="outlined"
                  style={{ height: '20px', fontSize: '0.65rem', minWidth: '28px' }}
                />
                <Chip
                  label={person.status === 'ALIVE' ? '●' : '○'}
                  size="small"
                  color={person.status === 'ALIVE' ? 'success' : 'default'}
                  variant="outlined"
                  style={{ height: '20px', fontSize: '0.65rem', minWidth: '28px' }}
                  title={person.status}
                />
                {person.birthOrder && (
                  <Chip
                    label={`#${person.birthOrder}`}
                    size="small"
                    style={{ 
                      height: '20px', 
                      fontSize: '0.65rem', 
                      backgroundColor: '#E8F5E9',
                      color: '#2E7D32'
                    }}
                    title={`Birth order: ${person.birthOrder}`}
                  />
                )}
              </Box>
            </Box>

            {canEdit && (
              <Box display="flex" gap="0px" marginLeft="4px">
                <IconButton onClick={handleEditClick} size="small" color="primary" style={{ padding: '4px' }}>
                  <EditIcon style={{ fontSize: '18px' }} />
                </IconButton>
                <IconButton onClick={handleDeleteClick} size="small" color="error" style={{ padding: '4px' }}>
                  <DeleteIcon style={{ fontSize: '18px' }} />
                </IconButton>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    );
  }

  // Desktop table row
  return (
    <Box
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid #E5E7EB',
        cursor: 'pointer',
        transition: 'background-color 0.15s',
        backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F9FAFB',
      }}
      onClick={() => onSelect(person)}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#F3F4F6';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#FFFFFF' : '#F9FAFB';
      }}
    >
      {/* Generation Indicator */}
      <Box style={{ width: '4px', height: '100%', backgroundColor: genColor }} />

      {/* Avatar */}
      <Box style={{ padding: '12px 16px', minWidth: '70px' }}>
        <Avatar
          src={person.photoPath ? photoThumb(person.photoPath, 64) : undefined}
          alt={person.name}
          style={{
            width: '36px',
            height: '36px',
            backgroundColor: genColor,
            color: '#fff',
            fontWeight: '600',
            fontSize: '1rem',
          }}
        >
          {genderIcon}
        </Avatar>
      </Box>

      {/* Name */}
      <Box style={{ flex: 2, padding: '12px 16px', minWidth: '180px' }}>
        <Box display="flex" alignItems="center" gap="8px">
          <Typography style={{ fontWeight: '500', fontSize: '0.9rem' }}>
            {person.name}
          </Typography>
          {person.birthOrder && (
            <Chip
              label={`#${person.birthOrder}`}
              size="small"
              style={{
                height: '18px',
                fontSize: '0.65rem',
                backgroundColor: '#E8F5E9',
                color: '#2E7D32',
              }}
              title={`Birth order among siblings: ${person.birthOrder}`}
            />
          )}
        </Box>
      </Box>

      {/* Generation */}
      <Box style={{ flex: 0.8, padding: '12px 16px', minWidth: '100px' }}>
        <Chip
          label={`Gen ${generation + 1}`}
          size="small"
          style={{
            backgroundColor: genColor,
            color: '#fff',
            fontWeight: '500',
            fontSize: '0.75rem',
          }}
        />
      </Box>

      {/* Gender */}
      <Box style={{ flex: 0.8, padding: '12px 16px', minWidth: '90px' }}>
        <Typography style={{ fontSize: '0.85rem', color: '#6B7280' }}>
          {person.gender || 'N/A'}
        </Typography>
      </Box>

      {/* Status */}
      <Box style={{ flex: 0.8, padding: '12px 16px', minWidth: '90px' }}>
        <Chip
          label={person.status || 'N/A'}
          size="small"
          color={person.status === 'ALIVE' ? 'success' : 'default'}
          variant="outlined"
          style={{ fontSize: '0.75rem' }}
        />
      </Box>

      {/* Role */}
      <Box style={{ flex: 1, padding: '12px 16px', minWidth: '120px' }}>
        <Typography style={{ fontSize: '0.85rem', color: '#6B7280' }}>
          {person.role || '-'}
        </Typography>
      </Box>

      {/* Actions — only visible to the admin or a designated family editor */}
      {canEdit && (
        <Box style={{ padding: '12px 16px', minWidth: '90px', display: 'flex', gap: '4px' }}>
          <IconButton onClick={handleEditClick} size="small" color="primary">
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton onClick={handleDeleteClick} size="small" color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
});

// Print Report Component
const PrintReport = ({ people, generations, title, onClose }) => {
  const printRef = useRef(null);
  
  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${title} - Mwendwa Kitavi Family</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 20px; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 15px; }
          .header h1 { font-size: 24px; color: #1a1a1a; margin-bottom: 5px; }
          .header h2 { font-size: 16px; color: #666; font-weight: normal; }
          .header .date { font-size: 12px; color: #888; margin-top: 5px; }
          .stats { display: flex; justify-content: center; gap: 30px; margin-bottom: 25px; flex-wrap: wrap; }
          .stat-box { text-align: center; padding: 10px 20px; background: #f5f5f5; border-radius: 8px; }
          .stat-box .number { font-size: 24px; font-weight: bold; color: #333; }
          .stat-box .label { font-size: 12px; color: #666; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #333; color: white; padding: 12px 8px; text-align: left; font-size: 12px; text-transform: uppercase; }
          td { padding: 10px 8px; border-bottom: 1px solid #ddd; font-size: 13px; }
          tr:nth-child(even) { background: #f9f9f9; }
          .gen-badge { display: inline-block; padding: 3px 10px; border-radius: 12px; color: white; font-size: 11px; font-weight: 500; }
          .status-alive { color: #22c55e; font-weight: 500; }
          .status-dead { color: #666; }
          .footer { margin-top: 30px; text-align: center; font-size: 11px; color: #888; border-top: 1px solid #ddd; padding-top: 15px; }
          @media print {
            body { padding: 10px; }
            .header { margin-bottom: 20px; }
            table { font-size: 11px; }
            th, td { padding: 6px 4px; }
          }
        </style>
      </head>
      <body>
        ${printContent.innerHTML}
      </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };
  
  // Sort by generation, then by name
  const sortedPeople = [...people].sort((a, b) => {
    const genA = generations[a._id] || 0;
    const genB = generations[b._id] || 0;
    if (genA !== genB) return genA - genB;
    return a.name.localeCompare(b.name);
  });
  
  const totalAlive = people.filter(p => p.status === 'ALIVE').length;
  const totalDead = people.filter(p => p.status === 'DEAD').length;
  const totalMale = people.filter(p => p.gender === 'MALE').length;
  const totalFemale = people.filter(p => p.gender === 'FEMALE').length;
  
  return (
    <Dialog open={true} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{title}</Typography>
          <Box>
            <Button 
              variant="contained" 
              startIcon={<PrintIcon />} 
              onClick={handlePrint}
              style={{ marginRight: '10px' }}
            >
              Print
            </Button>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <div ref={printRef}>
          <div className="header">
            <h1>Mwendwa Kitavi Family Tree</h1>
            <h2>{title}</h2>
            <div className="date">Generated on {new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</div>
          </div>
          
          <div className="stats">
            <div className="stat-box">
              <div className="number">{people.length}</div>
              <div className="label">Total Members</div>
            </div>
            <div className="stat-box">
              <div className="number" style={{ color: '#22c55e' }}>{totalAlive}</div>
              <div className="label">Alive</div>
            </div>
            <div className="stat-box">
              <div className="number" style={{ color: '#666' }}>{totalDead}</div>
              <div className="label">Deceased</div>
            </div>
            <div className="stat-box">
              <div className="number" style={{ color: '#3b82f6' }}>{totalMale}</div>
              <div className="label">Male</div>
            </div>
            <div className="stat-box">
              <div className="number" style={{ color: '#ec4899' }}>{totalFemale}</div>
              <div className="label">Female</div>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th style={{ width: '5%' }}>#</th>
                <th style={{ width: '35%' }}>Name</th>
                <th style={{ width: '15%' }}>Generation</th>
                <th style={{ width: '15%' }}>Gender</th>
                <th style={{ width: '15%' }}>Status</th>
                <th style={{ width: '15%' }}>Role</th>
              </tr>
            </thead>
            <tbody>
              {sortedPeople.map((person, idx) => {
                const gen = generations[person._id] || 0;
                const genColor = GENERATION_COLORS[Math.min(gen, GENERATION_COLORS.length - 1)].color;
                return (
                  <tr key={person._id}>
                    <td>{idx + 1}</td>
                    <td style={{ fontWeight: '500' }}>{person.name}</td>
                    <td>
                      <span 
                        className="gen-badge" 
                        style={{ backgroundColor: genColor }}
                      >
                        Gen {gen + 1}
                      </span>
                    </td>
                    <td>{person.gender || '-'}</td>
                    <td className={person.status === 'ALIVE' ? 'status-alive' : 'status-dead'}>
                      {person.status || '-'}
                    </td>
                    <td>{person.role || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          
          <div className="footer">
            <p>Mwendwa Kitavi Family Tree Database • Total Records: {people.length}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main component
const FamilyMembersTable = ({ people = [], onEdit, onDelete, onSelect, onBack, isMobile, canEdit = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPeople, setFilteredPeople] = useState([]);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [filterGen, setFilterGen] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterGender, setFilterGender] = useState('all');
  const [showReport, setShowReport] = useState(false);
  const [reportTitle, setReportTitle] = useState('');
  const [reportPeople, setReportPeople] = useState([]);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const containerRef = useRef(null);
  const headerRef = useRef(null);

  const generations = useMemo(() => calculateGenerations(people || []), [people]);
  const generationStats = useMemo(() => getGenerationStats(people || [], generations), [people, generations]);
  
  // Get unique generations for filter
  const uniqueGenerations = useMemo(() => {
    const gens = new Set(Object.values(generations));
    return Array.from(gens).sort((a, b) => a - b);
  }, [generations]);

  // Filter people
  useEffect(() => {
    if (!people || !Array.isArray(people)) {
      setFilteredPeople([]);
      return;
    }
    
    let filtered = [...people];
    
    // Search filter
    if (searchTerm.trim() !== '') {
      filtered = filtered.filter((person) =>
        person.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Generation filter
    if (filterGen !== 'all') {
      const genNum = parseInt(filterGen);
      filtered = filtered.filter(p => generations[p._id] === genNum);
    }
    
    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(p => p.status === filterStatus);
    }
    
    // Gender filter
    if (filterGender !== 'all') {
      filtered = filtered.filter(p => p.gender === filterGender);
    }
    
    // Sort by generation, then birthOrder, then name
    filtered.sort((a, b) => {
      const genA = generations[a._id] || 0;
      const genB = generations[b._id] || 0;
      if (genA !== genB) return genA - genB;
      
      // Within same generation, sort by birthOrder (if available)
      const orderA = a.birthOrder ?? 9999;
      const orderB = b.birthOrder ?? 9999;
      if (orderA !== orderB) return orderA - orderB;
      
      return a.name.localeCompare(b.name);
    });
    
    setFilteredPeople(filtered);
  }, [searchTerm, people, filterGen, filterStatus, filterGender, generations]);

  // Update dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current && headerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: containerRect.width,
          height: containerRect.height,
        });
      }
    };

    updateDimensions();
    const resizeObserver = new ResizeObserver(updateDimensions);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    return () => resizeObserver.disconnect();
  }, []);

  const handleConfirmDelete = () => {
    if (pendingAction && pendingAction.type === 'delete') {
      onDelete(pendingAction.person);
    }
    setPendingAction(null);
    setConfirmDeleteOpen(false);
  };

  const getListHeight = () => {
    if (dimensions.height <= 0) return 0;
    const headerSubtraction = isMobile ? 0 : 48;
    return Math.max(0, dimensions.height - headerSubtraction);
  };
  
  // Generate report
  const handleGenerateReport = (type, value) => {
    let title = '';
    let reportData = [];
    
    switch (type) {
      case 'generation':
        const genNum = parseInt(value);
        title = `Generation ${genNum + 1} Report`;
        reportData = people.filter(p => generations[p._id] === genNum);
        break;
      case 'alive':
        title = 'Living Members Report';
        reportData = people.filter(p => p.status === 'ALIVE');
        break;
      case 'dead':
        title = 'Deceased Members Report';
        reportData = people.filter(p => p.status === 'DEAD');
        break;
      case 'male':
        title = 'Male Members Report';
        reportData = people.filter(p => p.gender === 'MALE');
        break;
      case 'female':
        title = 'Female Members Report';
        reportData = people.filter(p => p.gender === 'FEMALE');
        break;
      case 'all':
        title = 'Complete Family Report';
        reportData = people;
        break;
      case 'current':
        title = `Filtered Report (${filteredPeople.length} members)`;
        reportData = filteredPeople;
        break;
      default:
        title = 'Family Report';
        reportData = people;
    }
    
    setReportTitle(title);
    setReportPeople(reportData);
    setShowReport(true);
    setFilterMenuAnchor(null);
  };
  
  const clearFilters = () => {
    setFilterGen('all');
    setFilterStatus('all');
    setFilterGender('all');
    setSearchTerm('');
  };
  
  const hasActiveFilters = filterGen !== 'all' || filterStatus !== 'all' || filterGender !== 'all' || searchTerm.trim() !== '';

  return (
    <>
      {/* Print Report Dialog */}
      {showReport && (
        <PrintReport 
          people={reportPeople} 
          generations={generations} 
          title={reportTitle}
          onClose={() => setShowReport(false)}
        />
      )}
      
      {/* Confirm delete — real permission check already happened server-side;
          this is just the "are you sure?" for a serious, irreversible action */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => { setConfirmDeleteOpen(false); setPendingAction(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove {pendingAction?.person?.name || 'this person'}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes them from the family tree. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirmDeleteOpen(false); setPendingAction(null); }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Main Container */}
      <Box
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: isMobile ? 'calc(100vh - 60px)' : '100vh', // Account for mobile bottom nav
          backgroundColor: '#F3F4F6',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box
          ref={headerRef}
          style={{
            backgroundColor: '#FFFFFF',
            borderBottom: '1px solid #E5E7EB',
            padding: isMobile ? '12px 16px' : '16px 24px',
            flexShrink: 0,
          }}
        >
          {/* Title Row */}
          <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="12px">
            <Box>
              <Typography variant="h5" style={{ fontWeight: '700', color: '#111827', fontSize: isMobile ? '1.2rem' : '1.5rem' }}>
                Family Members
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {filteredPeople.length} of {people.length} members
                {hasActiveFilters && ' (filtered)'}
              </Typography>
            </Box>
            <Box display="flex" gap="8px">
              {/* Report Menu */}
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
                size={isMobile ? 'small' : 'medium'}
              >
                {isMobile ? '' : 'Reports'}
              </Button>
              <Menu
                anchorEl={filterMenuAnchor}
                open={Boolean(filterMenuAnchor)}
                onClose={() => setFilterMenuAnchor(null)}
              >
                <MenuItem onClick={() => handleGenerateReport('all')}>
                  📋 Complete Family Report
                </MenuItem>
                {hasActiveFilters && (
                  <MenuItem onClick={() => handleGenerateReport('current')}>
                    🔍 Current Filter Results
                  </MenuItem>
                )}
                <MenuItem disabled style={{ opacity: 0.6, fontSize: '0.8rem' }}>
                  ── By Generation ──
                </MenuItem>
                {uniqueGenerations.map(gen => (
                  <MenuItem key={gen} onClick={() => handleGenerateReport('generation', gen)}>
                    Generation {gen + 1} ({generationStats[gen]?.total || 0} members)
                  </MenuItem>
                ))}
                <MenuItem disabled style={{ opacity: 0.6, fontSize: '0.8rem' }}>
                  ── By Status ──
                </MenuItem>
                <MenuItem onClick={() => handleGenerateReport('alive')}>
                  🟢 Living Members ({people.filter(p => p.status === 'ALIVE').length})
                </MenuItem>
                <MenuItem onClick={() => handleGenerateReport('dead')}>
                  ⚫ Deceased Members ({people.filter(p => p.status === 'DEAD').length})
                </MenuItem>
                <MenuItem disabled style={{ opacity: 0.6, fontSize: '0.8rem' }}>
                  ── By Gender ──
                </MenuItem>
                <MenuItem onClick={() => handleGenerateReport('male')}>
                  ♂️ Male Members ({people.filter(p => p.gender === 'MALE').length})
                </MenuItem>
                <MenuItem onClick={() => handleGenerateReport('female')}>
                  ♀️ Female Members ({people.filter(p => p.gender === 'FEMALE').length})
                </MenuItem>
              </Menu>
              
              {onBack && (
                <IconButton onClick={onBack}>
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
          </Box>

          {/* Search and Filters Row */}
          <Box display="flex" gap={isMobile ? "8px" : "12px"} flexWrap="wrap" alignItems="center">
            {/* Search */}
            <TextField
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              style={{ flex: 1, minWidth: isMobile ? '140px' : '200px' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            {/* Generation Filter */}
            <FormControl size="small" style={{ minWidth: isMobile ? '90px' : '120px' }}>
              <InputLabel style={{ fontSize: isMobile ? '0.8rem' : '1rem' }}>Gen</InputLabel>
              <Select
                value={filterGen}
                onChange={(e) => setFilterGen(e.target.value)}
                label="Gen"
                style={{ fontSize: isMobile ? '0.8rem' : '1rem' }}
              >
                <MenuItem value="all">All</MenuItem>
                {uniqueGenerations.map(gen => (
                  <MenuItem key={gen} value={gen}>
                    G{gen + 1} ({generationStats[gen]?.total || 0})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Status Filter */}
            <FormControl size="small" style={{ minWidth: isMobile ? '80px' : '100px' }}>
              <InputLabel style={{ fontSize: isMobile ? '0.8rem' : '1rem' }}>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Status"
                style={{ fontSize: isMobile ? '0.8rem' : '1rem' }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="ALIVE">Alive</MenuItem>
                <MenuItem value="DEAD">Dead</MenuItem>
              </Select>
            </FormControl>
            
            {/* Gender Filter - Hide on very small screens, show as icon button */}
            {!isMobile && (
              <FormControl size="small" style={{ minWidth: '100px' }}>
                <InputLabel>Gender</InputLabel>
                <Select
                  value={filterGender}
                  onChange={(e) => setFilterGender(e.target.value)}
                  label="Gender"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="MALE">Male</MenuItem>
                  <MenuItem value="FEMALE">Female</MenuItem>
                </Select>
              </FormControl>
            )}
            
            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button 
                variant="text" 
                size="small" 
                onClick={clearFilters}
                style={{ color: '#EF4444' }}
              >
                Clear
              </Button>
            )}
          </Box>
        </Box>

        {/* Table Container */}
        <Box
          ref={containerRef}
          style={{
            flex: 1,
            overflow: 'hidden',
            padding: isMobile ? '8px' : '12px',
            minHeight: 0,
          }}
        >
          <Paper
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
            elevation={0}
          >
            {/* Table Header (Desktop) */}
            {!isMobile && (
              <Box
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: '#F9FAFB',
                  borderBottom: '2px solid #E5E7EB',
                  minHeight: '48px',
                  flexShrink: 0,
                }}
              >
                <Box style={{ width: '4px' }} />
                <Box style={{ padding: '12px 16px', minWidth: '70px' }}>
                  <Typography style={{ fontSize: '0.7rem', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>
                    
                  </Typography>
                </Box>
                <Box style={{ flex: 2, padding: '12px 16px', minWidth: '180px' }}>
                  <Typography style={{ fontSize: '0.7rem', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>
                    Name
                  </Typography>
                </Box>
                <Box style={{ flex: 0.8, padding: '12px 16px', minWidth: '100px' }}>
                  <Typography style={{ fontSize: '0.7rem', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>
                    Generation
                  </Typography>
                </Box>
                <Box style={{ flex: 0.8, padding: '12px 16px', minWidth: '90px' }}>
                  <Typography style={{ fontSize: '0.7rem', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>
                    Gender
                  </Typography>
                </Box>
                <Box style={{ flex: 0.8, padding: '12px 16px', minWidth: '90px' }}>
                  <Typography style={{ fontSize: '0.7rem', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>
                    Status
                  </Typography>
                </Box>
                <Box style={{ flex: 1, padding: '12px 16px', minWidth: '120px' }}>
                  <Typography style={{ fontSize: '0.7rem', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>
                    Role
                  </Typography>
                </Box>
                <Box style={{ padding: '12px 16px', minWidth: '90px' }}>
                  <Typography style={{ fontSize: '0.7rem', fontWeight: '600', color: '#6B7280', textTransform: 'uppercase' }}>
                    Actions
                  </Typography>
                </Box>
              </Box>
            )}

            {/* List */}
            {filteredPeople.length > 0 ? (
              <FixedSizeList
                height={getListHeight()}
                width={dimensions.width}
                itemCount={filteredPeople.length}
                itemSize={isMobile ? 72 : 56}
                itemData={{
                  people: filteredPeople,
                  onSelect,
                  setConfirmDeleteOpen,
                  setPendingAction,
                  isMobile,
                  generations,
                  canEdit,
                  onEdit,
                }}
              >
                {Row}
              </FixedSizeList>
            ) : (
              <Box
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#9CA3AF',
                }}
              >
                <Box textAlign="center">
                  <PersonIcon style={{ fontSize: '48px', marginBottom: '8px' }} />
                  <Typography>No members found</Typography>
                  {hasActiveFilters && (
                    <Button 
                      variant="text" 
                      onClick={clearFilters}
                      style={{ marginTop: '10px' }}
                    >
                      Clear Filters
                    </Button>
                  )}
                </Box>
              </Box>
            )}
          </Paper>
        </Box>

        {/* Footer Legend */}
        <Box
          style={{
            backgroundColor: '#FFFFFF',
            borderTop: '1px solid #E5E7EB',
            padding: isMobile ? '10px 16px' : '12px 24px',
            paddingBottom: isMobile ? '20px' : '12px',
            flexShrink: 0,
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={isMobile ? "6px" : "10px"}>
            <Box display="flex" gap="4px" flexWrap="wrap" alignItems="center">
              {!isMobile && (
                <Typography variant="caption" style={{ color: '#6B7280', fontWeight: '600', marginRight: '8px' }}>
                  GENERATIONS:
                </Typography>
              )}
              {uniqueGenerations.slice(0, isMobile ? 5 : 8).map(gen => {
                const genColor = GENERATION_COLORS[Math.min(gen, GENERATION_COLORS.length - 1)].color;
                return (
                  <Chip
                    key={gen}
                    label={isMobile ? `G${gen + 1}` : `G${gen + 1}: ${generationStats[gen]?.total || 0}`}
                    size="small"
                    style={{
                      backgroundColor: genColor,
                      color: '#fff',
                      fontWeight: '500',
                      fontSize: isMobile ? '0.6rem' : '0.7rem',
                      height: isMobile ? '20px' : '24px',
                    }}
                    onClick={() => setFilterGen(gen.toString())}
                  />
                );
              })}
            </Box>
            {!isMobile && (
              <Typography variant="caption" style={{ color: '#9CA3AF' }}>
                Click generation to filter
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

// Cloudinary face-centered thumbnail: small, fast, cropped to the face
const photoThumb = (url, size = 80) =>
  url && typeof url === 'string' && url.includes('/upload/')
    ? url.replace('/upload/', `/upload/w_${size},h_${size},c_fill,g_face/`)
    : url;


export default FamilyMembersTable;
